import { translations } from '../translations/translations';

const hexChars = '0123456789abcdef';

const randomHex = () => hexChars[Math.floor(Math.random() * 16)];

export const newGuid = () =>
    [...'00000000-0000-0000-0000-000000000000']
        .map((char) => (char === '0' ? randomHex() : char))
        .join('');

/** Turn the the base64 SVG into an image src.
 *
 * TODO:
 * Replace the '' with some sort of a default icon if no other given,
 * or send the default also from the API? */
export const base64SvgToSrc = (base64Svg: string): string =>
    base64Svg ? `data:image/svg+xml;base64,${base64Svg}` : '';

export const base64JpgToSrc = (base64Jpg: string): string =>
    base64Jpg ? `data:image/jpeg;base64,${base64Jpg}` : '';

export const base64toFile = (base64: string, filename: string): File => {
    const dataUrl = base64JpgToSrc(base64);
    const data = dataUrl.split(',');
    const mime = data[0].match(/:(.*?);/)![1];
    const decodedData = atob(data[data.length - 1]);
    let n = decodedData.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = decodedData.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};

export const isString = (value: unknown): value is string => {
    return typeof value === 'string';
};

export const isArray = (value: unknown): value is [] => {
    return Array.isArray(value);
};

export const isObject = (value: unknown): value is Record<string, unknown> => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

// Escape string for use in a RegEx
// https://stackoverflow.com/questions/3561493/is-there-a-regexp-escape-function-in-javascript/3561711#3561711
export const escapeStringForRegex = (str: string) => str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

export const replaceAll = (value: string, substring: string, newSubstring: string) =>
    value.replace(RegExp(escapeStringForRegex(substring), 'g'), newSubstring);

export const stringReplace = (template: string, ...replacementParameters: unknown[]) =>
    replacementParameters.length > 0
        ? (replacementParameters.reduce((reduceString, parameter) => {
              // / \${.*?} /
              // \$ matches the character $ literally (case sensitive)
              // { matches the character { literally (case sensitive)
              // .*? matches any character (except for line terminators)
              // *? Quantifier — Matches between zero and unlimited times, as few times as possible, expanding as needed (lazy)
              // } matches the character } literally (case sensitive)
              const valueReferenceRegEx = /\${.*?}/;

              // This function accepts different types of parameters
              // e.g. stringReplace('${test}','replaceFirstReference','replaceSecondReference')

              if (isString(reduceString) && isString(parameter)) {
                  return reduceString.replace(valueReferenceRegEx, parameter);
              }

              // e.g. stringReplace('${test}',['replaceFirstReference','replaceSecondReference'])
              if (isArray(parameter)) {
                  return parameter.reduce((reduceStringList, value) => {
                      if (!isString(reduceStringList)) {
                          return '';
                      }

                      return reduceStringList.replace(valueReferenceRegEx, value);
                  }, reduceString);
              }

              // e.g. stringReplace('${test}',{test,'replace'})
              if (isObject(parameter)) {
                  return Object.entries(parameter).reduce((reduceStringObject, [key, value]) => {
                      if (!isString(reduceStringObject)) {
                          return '';
                      }

                      return replaceAll(
                          reduceStringObject,
                          `\${${key}}`,
                          !isString(value) ? String(value) : value,
                      );
                  }, reduceString);
              }

              // any invalid case
              return reduceString;
          }, template) as string)
        : template;

export const tryGetErrorMessage = (error: unknown, language: string) => {
    return error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : translations[language].ERROR_unknown;
};
